import moment, {Moment} from 'moment';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm';

export const today = (): string => toISODateString(moment());

/**
 * Dummy-type to explicitly state that a *local* date (time) string is expected.
 * That means, there must be no time zone in the string and that you have to be
 * extra careful to not use it with the `Date` constructor.
 *
 * @see https://stackoverflow.com/questions/51715259/what-are-valid-date-time-strings-in-javascript
 */
type LocalDateTimeString = string;

export const secondsToTimeDuration = (durationInSeconds: number): string =>
  secondsToTimeDurationNumber(durationInSeconds).toFixed(2);

export const secondsToTimeDurationNumber = (
  durationInSeconds: number
): number => durationInSeconds / 3600;

/**
 * Return the duration in rounded hours and minutes
 */
export const getRoundedHoursAndMins = (
  duration: moment.Duration
): {hours: number; minutes: number} => {
  let hours = Math.floor(duration.asHours());
  let minutes = duration.minutes() + (duration.seconds() >= 30 ? 1 : 0);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  return {hours, minutes};
};

/**
 * Converts industry hours to seconds. It does so by removing all decimal places except
 * the first two, and then converts it into seconds.
 */
export const industryHoursToSeconds = (hours: number | string): number => {
  if (typeof hours === 'string') {
    // eslint-disable-next-line no-param-reassign
    hours = Number(hours);
  }
  // eslint-disable-next-line no-param-reassign
  hours = Number(hours.toFixed(2));

  return moment.duration(hours, 'h').asSeconds();
};

type DateAsString = {value: string; format: string};

export const momentOf = (date: DateAsString, time: DateAsString): Moment =>
  moment(`${date.value} ${time.value}`, `${date.format} ${time.format}`);

export const convertToMoment = (value?: string): moment.Moment | null =>
  value ? moment(value, 'YYYY-MM-DD') : null;

/**
 * Formats a moment.Moment into a *local* ISO date and time
 * string without any time zone (e.g. "2021-06-30T00:00:00").
 */
export const momentToDateTime = (
  localDate: moment.Moment
): LocalDateTimeString => localDate.format('YYYY-MM-DD[T]HH:mm:ss');

export const toISODateString = (date: Moment): string =>
  date.format('YYYY-MM-DD');

export const toISODateStringOrUndefined = (
  value: moment.Moment | null | undefined
): string | undefined => {
  if (!value) {
    return;
  }
  return value.isValid() ? value.format('YYYY-MM-DD') : undefined;
};

/**
 * Converts a moment into a string parseable by java/kotlin Instant.
 */
export const momentToInstantString = (date: Moment): string =>
  date.utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');

/**
 * Converts an array of numbers into the total amount of
 * hours for an ActivitySuggestion or ActivityItem.
 */
export const toTotalHours = (items: number[]): number =>
  Math.round(
    items.reduce((accumulator, currentValue) => accumulator + currentValue, 0) *
      100
  ) / 100 || 0;
